// src/pages/Billing.js
// Billing & Subscriptions page with Stripe integration

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts';
import { createCheckoutSession, createPortalSession, getBillingStatus, connectOnboard, connectStatus, connectDashboard, connectDisconnect, clearStripeCustomer } from '../services/awsApiService';
import {
  CreditCard,
  Check,
  Zap,
  Building2,
  Crown,
  AlertCircle,
  ExternalLink,
  Calendar,
  Receipt,
  Settings,
  Landmark,
  Link,
  Unlink,
  CheckCircle,
  XCircle
} from 'lucide-react';

// Pricing tiers configuration
const PRICING_TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 349,
    interval: 'month',
    description: 'For solo practices (1-2 providers)',
    icon: Zap,
    color: 'teal',
    popular: false,
    customPricing: false,
    features: [
      'All 7 AI-Powered Workflows',
      '500 AI actions/month',
      'PMS bot integration (CSV import)',
      'Claim scrubbing & coding AI',
      'Automated denial appeals',
      'ERA auto-posting',
      'Up to 5 users',
      'Email support',
      '6-year HIPAA data retention'
    ]
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 549,
    interval: 'month',
    description: 'For growing practices (3-5 providers)',
    icon: Building2,
    color: 'blue',
    popular: true,
    customPricing: false,
    features: [
      'Everything in Starter, PLUS:',
      '2,000 AI actions/month',
      '200 benefits verifications/month',
      'Open Dental API sync',
      'EDI 278 pre-auth automation',
      'Up to 15 users',
      'Up to 3 locations',
      'Priority support',
      'Monthly performance reports'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 799,
    interval: 'month',
    description: 'For large practices & billing companies',
    icon: Crown,
    color: 'purple',
    popular: false,
    customPricing: false,
    features: [
      'Everything in Professional, PLUS:',
      '5,000 AI actions/month',
      '500 benefits verifications/month',
      'Unlimited locations',
      'Up to 25 users',
      'Dedicated account manager',
      'Custom reporting & analytics',
      'API access',
      'White-glove onboarding'
    ]
  },
  {
    id: 'dso',
    name: 'DSO / Custom',
    price: null,
    interval: 'month',
    description: 'For DSOs & multi-location groups (9+ locations)',
    icon: Crown,
    color: 'indigo',
    popular: false,
    customPricing: true,
    features: [
      'Everything in Enterprise, PLUS:',
      'Custom AI usage limits',
      'Custom benefits verification volume',
      'Multi-org management dashboard',
      'Priority support with SLA guarantee',
      'Quarterly business reviews',
      'Custom contract & annual pricing',
      'Dedicated implementation team'
    ]
  }
];

const Billing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, practice } = useAuth();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [connectInfo, setConnectInfo] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  // Handle Stripe checkout and Connect return URLs
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSuccess('Payment successful! Your subscription has been updated.');
      setSearchParams({});
      loadBillingInfo();
    } else if (searchParams.get('canceled') === 'true') {
      setError('Payment was canceled. No changes were made to your subscription.');
      setSearchParams({});
    } else if (searchParams.get('connect') === 'success') {
      setSuccess('Bank account connected! Patient payments will now route directly to your practice.');
      setSearchParams({});
      loadConnectStatus();
    } else if (searchParams.get('connect') === 'refresh') {
      setError('Bank account setup was not completed. Please try connecting again.');
      setSearchParams({});
    }
  }, [searchParams]);

  useEffect(() => {
    loadBillingInfo();
    loadConnectStatus();
  }, [practice]);

  const loadConnectStatus = async () => {
    try {
      const result = await connectStatus();
      if (result.success) setConnectInfo(result.data);
    } catch (e) { /* ignore */ }
  };

  const handleConnectOnboard = async () => {
    setConnectLoading(true);
    setError(null);
    try {
      const result = await connectOnboard();
      if (result.success && result.data?.url) {
        window.location.href = result.data.url;
      } else {
        setError(result.error || 'Failed to start bank account setup. Please try again.');
      }
    } catch (e) {
      setError('Failed to connect bank account: ' + e.message);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleConnectDashboard = async () => {
    setConnectLoading(true);
    try {
      const result = await connectDashboard();
      if (result.success && result.data?.url) {
        window.open(result.data.url, '_blank');
      } else {
        setError(result.error || 'Could not open dashboard.');
      }
    } catch (e) {
      setError('Failed to open dashboard: ' + e.message);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleConnectDisconnect = async () => {
    if (!window.confirm('Disconnect your bank account? Patient payment links will stop routing to your practice until you reconnect.')) return;
    setConnectLoading(true);
    try {
      await connectDisconnect();
      setConnectInfo(null);
      setSuccess('Bank account disconnected.');
    } catch (e) {
      setError('Failed to disconnect: ' + e.message);
    } finally {
      setConnectLoading(false);
    }
  };

  const loadBillingInfo = async () => {
    setLoading(true);
    try {
      const result = await getBillingStatus();

      const tier = result.success && result.data?.subscription_tier ? result.data.subscription_tier : 'starter';
      setCurrentPlan(tier);

      const planName = tier.charAt(0).toUpperCase() + tier.slice(1);
      const planPrice = tier === 'enterprise' ? 799.00 : tier === 'professional' ? 549.00 : 349.00;
      // Mock billing history - in production, Stripe Portal shows real invoices
      setBillingHistory([
        { id: 1, date: '2026-02-01', amount: planPrice, status: 'paid', description: `${planName} Plan - February 2026` },
        { id: 2, date: '2026-01-01', amount: planPrice, status: 'paid', description: `${planName} Plan - January 2026` },
        { id: 3, date: '2025-12-01', amount: planPrice, status: 'paid', description: `${planName} Plan - December 2025` },
      ]);
    } catch (err) {
      setCurrentPlan('starter');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (planId === currentPlan) return;

    setProcessingPayment(true);
    setError(null);
    setSuccess(null);

    try {
      const plan = PRICING_TIERS.find(p => p.id === planId);
      const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

      if (!stripeKey) {
        setSuccess(`Demo Mode: Would upgrade to ${plan.name} plan ($${plan.price}/mo). Configure Stripe keys to enable real payments.`);
        setCurrentPlan(planId);
      } else {
        const result = await createCheckoutSession(planId);

        if (!result.success) throw new Error(result.error);

        if (result.data.url) {
          window.location.href = result.data.url;
        } else {
          throw new Error('No checkout URL returned');
        }
      }
    } catch (err) {
      setError('Failed to process plan change. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleClearStripeCustomer = async () => {
    const result = await clearStripeCustomer();
    if (result.success) setSuccess('Stripe customer reset. Try subscribing again.');
    else setError('Reset failed: ' + result.error);
  };

  const handleManageSubscription = async () => {
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

    if (!stripeKey) {
      setSuccess('Demo Mode: Would open Stripe Customer Portal to manage subscription.');
      return;
    }

    try {
      const result = await createPortalSession();

      if (!result.success) {
        setError(result.error || 'No subscription found. Please select a plan first.');
        return;
      }

      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      setError('Failed to open subscription portal. Please try again.');
    }
  };

  const getColorClasses = (color, isSelected) => {
    const colors = {
      teal: {
        bg: isSelected ? 'bg-teal-600' : 'bg-teal-50',
        border: isSelected ? 'border-teal-600' : 'border-teal-200',
        text: isSelected ? 'text-white' : 'text-teal-700',
        icon: isSelected ? 'text-white' : 'text-teal-600',
        button: 'bg-teal-600 hover:bg-teal-700'
      },
      blue: {
        bg: isSelected ? 'bg-blue-600' : 'bg-blue-50',
        border: isSelected ? 'border-blue-600' : 'border-blue-200',
        text: isSelected ? 'text-white' : 'text-blue-700',
        icon: isSelected ? 'text-white' : 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      purple: {
        bg: isSelected ? 'bg-purple-600' : 'bg-purple-50',
        border: isSelected ? 'border-purple-600' : 'border-purple-200',
        text: isSelected ? 'text-white' : 'text-purple-700',
        icon: isSelected ? 'text-white' : 'text-purple-600',
        button: 'bg-purple-600 hover:bg-purple-700'
      }
    };
    return colors[color] || colors.teal;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-slate-600 mt-4">Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Billing & Subscription</h1>
          <p className="text-sm text-slate-500">Manage your plan and payment methods</p>
        </div>
        <button
          onClick={handleManageSubscription}
          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <Settings className="w-4 h-4" />
          Manage Subscription
        </button>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 rounded-lg border bg-red-50 border-red-200 text-red-700 flex items-center gap-3">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <button onClick={handleClearStripeCustomer} className="ml-auto text-sm underline">Reset Stripe customer</button>
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 rounded-lg border bg-green-50 border-green-200 text-green-700 flex items-center gap-2">
            <Check className="w-5 h-5" />
            <span>{success}</span>
          </div>
        )}

        {/* Current Plan Banner */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <p className="text-slate-600 text-sm">Current Plan</p>
                <p className="text-2xl font-bold text-slate-900 capitalize">{currentPlan || 'Free Trial'}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-slate-600 text-sm">Next billing date</p>
              <p className="text-lg font-semibold text-slate-900">February 1, 2026</p>
            </div>
          </div>
        </div>

        {/* Pricing Tiers */}
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Choose Your Plan</h2>
        <div className="grid grid-cols-3 gap-6 mb-12">
          {PRICING_TIERS.map((tier) => {
            const isSelected = currentPlan === tier.id;
            const colors = getColorClasses(tier.color, isSelected);
            const Icon = tier.icon;

            return (
              <div
                key={tier.id}
                className={`relative rounded-xl border-2 ${colors.border} ${isSelected ? colors.bg : 'bg-white'} transition-all hover:shadow-lg`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-white/20' : `bg-${tier.color}-100`}`}>
                      <Icon className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    <h3 className={`text-xl font-bold ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                      {tier.name}
                    </h3>
                  </div>

                  <div className="mb-4">
                    {tier.customPricing ? (
                      <span className={`text-3xl font-bold ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                        Contact Us for Pricing
                      </span>
                    ) : (
                      <>
                        <span className={`text-4xl font-bold ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                          ${tier.price}
                        </span>
                        <span className={`${isSelected ? 'text-white/80' : 'text-slate-500'}`}>
                          /month
                        </span>
                      </>
                    )}
                  </div>

                  <p className={`mb-6 ${isSelected ? 'text-white/80' : 'text-slate-600'}`}>
                    {tier.description}
                  </p>

                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className={`w-5 h-5 flex-shrink-0 ${isSelected ? 'text-white' : 'text-green-500'}`} />
                        <span className={`text-sm ${isSelected ? 'text-white' : 'text-slate-700'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {tier.customPricing ? (
                    <a
                      href="mailto:support@flowrcmdental.com?subject=Enterprise%20Pricing%20Inquiry"
                      className={`w-full py-3 rounded-lg font-semibold transition-colors text-center block bg-purple-600 hover:bg-purple-700 text-white`}
                    >
                      Get a Custom Quote
                    </a>
                  ) : (
                    <button
                      onClick={() => handleSelectPlan(tier.id)}
                      disabled={isSelected || processingPayment}
                      className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                        isSelected
                          ? 'bg-white/20 text-white cursor-default'
                          : `${colors.button} text-white`
                      } ${processingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isSelected ? 'Current Plan' : processingPayment ? 'Processing...' : `Subscribe — $${tier.price}/mo`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Billing History */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-teal-600" />
              Billing History
            </h2>
          </div>

          {billingHistory.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No billing history yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Date</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Description</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Amount</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Status</th>
                    <th className="text-right py-3 px-6 text-sm font-semibold text-slate-600">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {billingHistory.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-4 px-6 text-slate-700">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {new Date(invoice.date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-slate-900">{invoice.description}</td>
                      <td className="py-4 px-6 text-slate-900 font-medium">${invoice.amount}</td>
                      <td className="py-4 px-6">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button className="text-teal-600 hover:text-teal-800 flex items-center gap-1 ml-auto">
                          <ExternalLink className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stripe Notice */}
        <div className="mt-8 p-4 bg-slate-100 rounded-lg text-center">
          <p className="text-slate-600 text-sm">
            Payments are securely processed by{' '}
            <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">
              Stripe
            </a>
            . Your payment information is never stored on our servers.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Billing;
